import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const tierLabel = {
  annual: "Annual",
  standard: "Standard",
  starter: "Starter",
};

export default function Founding() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/founding/status");
      setStatus(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <main className="max-w-md mx-auto px-6 py-24 text-center"><p className="marginalia">Reading the ledger…</p></main>;
  }
  if (!status?.enabled) {
    return (
      <main className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="fleuron mb-6">❦</div>
        <h1 className="font-display text-5xl">The founding cohort has been sealed.</h1>
        <p className="font-body text-ink-iron mt-4 text-lg">All founding spots have been claimed. You're still welcome to join the hermitage at the regular tiers.</p>
        <Link to="/pricing" className="wax-button mt-8 inline-block">See pricing</Link>
      </main>
    );
  }

  const hostFull = status.host.remaining === 0;
  const hermitFull = status.hermit.remaining === 0;
  const allFull = hostFull && hermitFull;

  return (
    <main>
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 pt-12 sm:pt-20 pb-12 text-center">
        <p className="label-overline">An open invitation</p>
        <h1 className="font-display text-5xl sm:text-7xl mt-4 leading-[1] tracking-tight">
          Become a <em className="text-wax-red">Founding</em> member.
        </h1>
        <div className="fleuron my-8">❦ ❦ ❦</div>
        <p className="font-body text-ink-iron text-xl max-w-2xl mx-auto leading-relaxed">
          We are giving the first hosts and hermits free subscriptions in exchange for one thing: <em>show up</em>. Post a listing. Write a letter. Help us prove that the hermitage can still exist.
        </p>
      </section>

      {/* Two-card counter */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Hosts */}
          <div className={`parchment-card ${hostFull ? "opacity-60" : "border-wax-red border-2"}`} data-testid="founding-host-card">
            <p className="label-overline text-wax-red">For Hosts · landowners</p>
            <h2 className="font-display text-4xl mt-3">
              {hostFull ? "Filled" : <><span data-testid="founding-host-remaining">{status.host.remaining}</span> spots left</>}
            </h2>
            <p className="font-body text-ink-marginalia text-sm mt-1">
              {status.host.taken} of {status.host.limit} claimed
            </p>
            <div className="my-5 h-2 w-full bg-rule overflow-hidden rounded-full">
              <div
                className="h-full bg-wax-red transition-all duration-500"
                style={{ width: `${Math.min(100, (status.host.taken / Math.max(1, status.host.limit)) * 100)}%` }}
              />
            </div>
            <p className="font-display italic text-2xl text-ink-iron mt-4">
              Free <strong>{tierLabel[status.host.tier] || status.host.tier}</strong> for <strong>{status.host.months} months</strong>.
            </p>
            <ul className="mt-5 space-y-2 font-body text-ink-iron text-sm">
              <li className="flex gap-2"><span className="text-wax-red">❦</span> Unlimited hermitage listings</li>
              <li className="flex gap-2"><span className="text-wax-red">❦</span> Featured placement in browse</li>
              <li className="flex gap-2"><span className="text-wax-red">❦</span> Founding Member badge on your profile</li>
              <li className="flex gap-2"><span className="text-wax-red">❦</span> Priority in the weekly digest</li>
              <li className="flex gap-2"><span className="text-wax-red">❦</span> No card. No catch.</li>
            </ul>
            {!hostFull && (
              <Link to="/sign-up?role=host" data-testid="founding-host-cta" className="wax-button w-full mt-6 inline-block text-center">
                Claim a Host spot
              </Link>
            )}
          </div>

          {/* Hermits */}
          <div className={`parchment-card ${hermitFull ? "opacity-60" : ""}`} data-testid="founding-hermit-card">
            <p className="label-overline">For Hermits · wandering souls</p>
            <h2 className="font-display text-4xl mt-3">
              {hermitFull ? "Filled" : <><span data-testid="founding-hermit-remaining">{status.hermit.remaining}</span> spots left</>}
            </h2>
            <p className="font-body text-ink-marginalia text-sm mt-1">
              {status.hermit.taken} of {status.hermit.limit} claimed
            </p>
            <div className="my-5 h-2 w-full bg-rule overflow-hidden rounded-full">
              <div
                className="h-full bg-moss transition-all duration-500"
                style={{ width: `${Math.min(100, (status.hermit.taken / Math.max(1, status.hermit.limit)) * 100)}%` }}
              />
            </div>
            <p className="font-display italic text-2xl text-ink-iron mt-4">
              Free <strong>{tierLabel[status.hermit.tier] || status.hermit.tier}</strong> for <strong>{status.hermit.months} months</strong>.
            </p>
            <ul className="mt-5 space-y-2 font-body text-ink-iron text-sm">
              <li className="flex gap-2"><span className="text-moss">❦</span> Unlimited letters of intent</li>
              <li className="flex gap-2"><span className="text-moss">❦</span> Featured profile in host searches</li>
              <li className="flex gap-2"><span className="text-moss">❦</span> Founding Member badge on your profile</li>
              <li className="flex gap-2"><span className="text-moss">❦</span> Priority in the weekly digest</li>
              <li className="flex gap-2"><span className="text-moss">❦</span> No card. No catch.</li>
            </ul>
            {!hermitFull && (
              <Link to="/sign-up?role=hermit" data-testid="founding-hermit-cta" className="ghost-button w-full mt-6 inline-block text-center">
                Claim a Hermit spot
              </Link>
            )}
          </div>
        </div>

        {allFull && (
          <p className="text-center font-display italic text-2xl text-ink-iron mt-10">
            The founding cohort is full. Thank you — you may still join at the regular tiers.
          </p>
        )}

        <p className="text-center font-body text-ink-marginalia text-sm italic mt-10">
          Counter updates every 20 seconds. Spots are honoured in the order signups arrive.
        </p>
      </section>

      {/* Why we're doing this */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-16 border-t border-rule">
        <p className="label-overline">Why we are giving it away</p>
        <h2 className="font-display text-4xl mt-3 mb-6">A marketplace without people is just a parchment in the wind.</h2>
        <div className="font-body text-lg text-ink-iron leading-relaxed space-y-4">
          <p>
            A hermitage requires two: someone with land, and someone willing to live quietly upon it. If only one side shows up, the whole thing is theatre.
          </p>
          <p>
            So we are skipping the income for now. The first 200 hosts and the first 100 hermits get to use Hearth &amp; Hermit free. In exchange we ask one small thing — <em>write your listing</em>, <em>send a letter</em>, <em>be part of the proof</em> that this can still work.
          </p>
          <p>
            When the cohort fills, the price returns to its plain numbers. Until then: the door is open, the kettle is on, and the seal is fresh.
          </p>
        </div>
      </section>
    </main>
  );
}
